import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {SECTIONS} from 'constants/NavConstants';
import doServiceWorkerFastRefresh from 'dev/doServiceWorkerFastRefresh';
import {notifyForError} from 'dev/service-worker-components/errors';
import FastRefreshSettingsModal from 'dev/service-worker-components/FastRefreshSettingsModal';
import logDevServiceWorkerMessage from 'dev/service-worker-interop/logDevServiceWorkerMessage';
import {BrowserMessage, MessageType, sendToServiceWorker} from 'dev/service-worker-interop/Message';
import NavBarIcon from 'nav/navbar/NavBarIcon';
import {ITEM_SIZE_SIDE_NAVBAR} from 'styles/StyleConstants';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SectionHeader from 'ui-library/menus/SectionHeader';
import runModal from 'ui-library/runModal';

function createMessageHandler({
  setClientId,
}: {
  setClientId: (clientId: string) => void;
}): ({data}: {data: BrowserMessage}) => void {
  return ({data: message}: {data: BrowserMessage}) => {
    if (message.messageType === MessageType.READY_FOR_FAST_REFRESH) {
      window.location.reload();
    } else if (message.messageType === MessageType.CLIENT_ID_RESPONSE) {
      setClientId(message.clientId);
    }
  };
}

function handleMessageError(event: Event): void {
  logDevServiceWorkerMessage('Failed to message service worker', {
    errors: [new Error(`Service worker message error: ${event.toString()}`)],
  });
}

function handleControllerChange(_: Event): void {
  notifyForError('Dev service worker has been updated, please reload.');
}

function DevToolsMenu({clientId}: {clientId: string | null}): JSX.Element {
  const refreshMenuOption = (
    <Option
      icon="fast-forward"
      onSelect={() => {
        doServiceWorkerFastRefresh();
      }}
    >
      Fast Refresh (Ctrl+Shift+R)
    </Option>
  );

  const settingsMenuOption = clientId ? (
    <Option
      icon="settings"
      onSelect={async () => {
        await runModal(FastRefreshSettingsModal, {clientId});
      }}
    >
      Cache Settings
    </Option>
  ) : (
    <Option>Loading...</Option>
  );

  return (
    <Menu className={css(styles.menu)} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
      <SectionHeader>Internal Admin</SectionHeader>
      <Option icon="add-to-team" href="/internal-admin">
        Home
      </Option>
      <Option icon="checklist" href="/internal-admin/configs">
        Configs
      </Option>
      <SectionHeader>Dev Service Worker</SectionHeader>
      {refreshMenuOption}
      {settingsMenuOption}
    </Menu>
  );
}

interface DevToolsIconProps {
  withUserMenuOffset?: boolean;
}

export default function DevToolsIcon(props: DevToolsIconProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);

  useEffect(() => {
    const handleMessage = createMessageHandler({setClientId});
    navigator.serviceWorker.addEventListener('message', handleMessage);
    navigator.serviceWorker.addEventListener('messageerror', handleMessageError);
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      navigator.serviceWorker.removeEventListener('messageerror', handleMessageError);
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  useEffect(() => {
    const sw = global.__activeDevServiceWorker;
    if (sw) {
      sendToServiceWorker(sw, {
        messageType: MessageType.CLIENT_ID_REQUEST,
      });
    }
  }, []);

  return (
    <AbsoluteDropdownContainer
      shouldCloseOnInsideClick={true}
      menu={<DevToolsMenu clientId={clientId} />}
      onRequestClose={() => {
        setIsOpen(false);
      }}
      open={isOpen}
    >
      <div className={props.withUserMenuOffset ? css(styles.itemOffset) : css(styles.itemBottom)}>
        <NavBarIcon
          icon="brackets"
          isActive={isOpen}
          onSectionClick={() => {
            setIsOpen(!isOpen);
          }}
          section={SECTIONS.DEVELOPER}
        />
      </div>
    </AbsoluteDropdownContainer>
  );
}

const styles = StyleSheet.create({
  itemBottom: {
    bottom: 0,
    width: ITEM_SIZE_SIDE_NAVBAR,
    height: ITEM_SIZE_SIDE_NAVBAR,
    position: 'fixed',
  },
  itemOffset: {
    bottom: ITEM_SIZE_SIDE_NAVBAR,
    width: ITEM_SIZE_SIDE_NAVBAR,
    height: ITEM_SIZE_SIDE_NAVBAR,
    position: 'fixed',
  },
  menu: {
    bottom: '0',
    left: '100%',
    position: 'absolute',
    top: 'auto',
  },
});
